import Axios from "axios";

export interface ProviderCommunicationThread {
    id: number;
    /** Null on a general thread, where the prescriber contacted the Hub with no patient. */
    patientId: number | null;
    prescriberNPI: string;
    prescriberFirstName?: string | null;
    prescriberLastName?: string | null;
    practiceId: number;
    isResolved: boolean;
    resolvedDate?: string | null;
    resolvedBy?: string | null;
    createdDate: string;
    lastMessageDate: string;
    lastMessageBody?: string | null;
    lastMessageInbound?: boolean | null;
    lastMessageAuthorUserName?: string | null;
    unreadCount: number;
    /** True while a live chat on this thread is still active. */
    hasActiveChatSession?: boolean;
    patientFirstName?: string | null;
    patientLastName?: string | null;
    patientDOB?: string | null;
}

/**
 * How a message reached the thread. Live chat and async messages share one thread so the
 * history stays a single continuous record, which is why the channel is a field on the
 * message rather than a separate list.
 */
export enum ProviderCommunicationSourceChannel {
    Async = 1,
    LiveChat = 2,
}

export interface ProviderCommunicationMessage {
    id: number;
    threadId: number;
    storeID?: number | null;
    rxNumber?: number | null;
    rfNumber?: number | null;
    inbound: boolean;
    body: string;
    authorUserName: string;
    authorType: number;
    createdDate: string;
    /** Absent on messages written before live chat existed, which are all async. */
    sourceChannel?: ProviderCommunicationSourceChannel | null;
}

export enum ProviderCommunicationChatSessionStatus {
    Active = 1,
    Completed = 2,
    /** Ended without ever being answered by a Hub agent. */
    Abandoned = 3,
}

export interface ProviderCommunicationChatSession {
    id: number;
    threadId: number;
    conversationSid: string;
    flexInteractionSid?: string | null;
    flexChannelSid?: string | null;
    taskSid?: string | null;
    prescriberUserName?: string | null;
    /** The Flex worker who answered; null until a reservation is accepted. */
    agentUserName?: string | null;
    status: ProviderCommunicationChatSessionStatus;
    startedDate: string;
    endedDate?: string | null;
}

export interface ThreadDetailResponse {
    thread: ProviderCommunicationThread;
    messages: ProviderCommunicationMessage[];
    /** Live-chat sessions in this thread, oldest first. Empty for async-only threads. */
    chatSessions?: ProviderCommunicationChatSession[];
}

export interface InitiateRequest {
    patientId: number;
    prescriberNPI: string;
    prescriberFirstName?: string | null;
    prescriberLastName?: string | null;
    practiceId?: number | null;
    storeID?: number | null;
    rxNumber?: number | null;
    rfNumber?: number | null;
    body: string;
}

export interface ReplyRequest {
    storeID?: number | null;
    rxNumber?: number | null;
    rfNumber?: number | null;
    body: string;
}

export interface PracticeOption {
    id: number;
    name?: string | null;
    isActive?: boolean;
}

const BASE_URL = "/api/ProviderCommunications";

export const providerCommunicationsService = {
    async listThreads(patientId?: number, includeResolved = false): Promise<ProviderCommunicationThread[]> {
        const response = await Axios.get<ProviderCommunicationThread[]>(`${BASE_URL}/threads`, {
            params: {
                patientId: patientId ?? undefined,
                includeResolved,
            },
        });
        return response.data;
    },

    async getThread(id: number): Promise<ThreadDetailResponse> {
        const response = await Axios.get<ThreadDetailResponse>(`${BASE_URL}/threads/${id}`);
        return response.data;
    },

    async getPracticesForNpi(npi: string): Promise<PracticeOption[]> {
        const response = await Axios.get<PracticeOption[]>(`${BASE_URL}/practices-for-npi`, {
            params: { npi },
        });
        return response.data;
    },

    async initiate(request: InitiateRequest): Promise<ThreadDetailResponse> {
        const response = await Axios.post<ThreadDetailResponse>(`${BASE_URL}/threads/initiate`, request);
        return response.data;
    },

    async reply(threadId: number, request: ReplyRequest): Promise<ThreadDetailResponse> {
        const response = await Axios.post<ThreadDetailResponse>(`${BASE_URL}/threads/${threadId}/messages`, request);
        return response.data;
    },

    async setResolved(threadId: number, isResolved: boolean): Promise<ProviderCommunicationThread> {
        const response = await Axios.post<ProviderCommunicationThread>(
            `${BASE_URL}/threads/${threadId}/resolve`,
            { isResolved }
        );
        return response.data;
    },

    async markRead(threadId: number): Promise<void> {
        await Axios.post(`${BASE_URL}/threads/${threadId}/mark-read`);
    },

    async getUnreadCount(): Promise<number> {
        const response = await Axios.get<{ count: number }>(`${BASE_URL}/unread-count`);
        return response.data.count;
    },
};

export const providerCommunicationsPermissions = {
    view: "Provider Communications/View",
    send: "Provider Communications/Send",
};
